import { supabase } from '@/lib/supabase';
import { SupplierReturn, PaginatedResult } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchSupplierReturnsParams {
  search?: string;
  supplierId?: string;
  status?: string;
  page?: number;
}

export async function fetchSupplierReturns(
  params: FetchSupplierReturnsParams = {}
): Promise<PaginatedResult<SupplierReturn>> {
  const { search = '', supplierId = '', status = '', page = 0 } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('supplier_returns')
    .select('*, supplier:suppliers(id, name, phone), purchase:purchases(id, purchase_number, supplier_invoice_number)', { count: 'exact' })
    .order('return_date', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    query = query.or(
      `return_number.ilike.%${search.trim()}%,reason.ilike.%${search.trim()}%,notes.ilike.%${search.trim()}%`
    );
  }

  if (supplierId) query = query.eq('supplier_id', supplierId);
  if (status) query = query.eq('status', status);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as SupplierReturn[], count: count ?? 0 };
}

export async function fetchSupplierReturnById(id: string): Promise<SupplierReturn> {
  const { data, error } = await supabase
    .from('supplier_returns')
    .select('*, supplier:suppliers(*), purchase:purchases(*), items:supplier_return_items(*, laptop:laptops(*), accessory:accessories(*))')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as SupplierReturn;
}

export interface ProcessSupplierReturnPayload {
  supplier_id: string;
  purchase_id?: string | null;
  items: Array<{
    item_type: 'laptop' | 'accessory';
    laptop_id?: string | null;
    accessory_id?: string | null;
    purchase_item_id?: string | null;
    quantity: number;
    unit_cost?: number | null;
    condition_on_return?: string | null;
    notes?: string | null;
  }>;
  reason: string;
  notes?: string | null;
  refund_amount?: number | null;
  refund_method?: string;
}

export async function processSupplierReturnRpc(payload: ProcessSupplierReturnPayload): Promise<{
  success: boolean;
  supplier_return_id: string;
  return_number: string;
  return_value: number;
  credit_applied_to_payable: number;
  refund_amount: number;
}> {
  const { data, error } = await supabase.rpc('process_supplier_return', {
    p_supplier_id: payload.supplier_id,
    p_purchase_id: payload.purchase_id ?? null,
    p_items: payload.items,
    p_reason: payload.reason.trim(),
    p_notes: payload.notes?.trim() || null,
    p_refund_amount: payload.refund_amount ?? null,
    p_refund_method: payload.refund_method ?? 'cash',
  });

  if (error) throw error;
  return data as any;
}
